import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Megaphone, Plus, Trash2, Trophy } from 'lucide-react';
import {
  AdminPage,
  Button,
  EmptyState,
  ErrorState,
  Panel,
  Skeleton,
  StatusBadge,
  Table,
  Td,
  Th,
  Tr,
  useUndo,
  useConfirm,
} from '@/design/components';
import { usePerm } from '@/access/usePerm';
import { formatDateTime } from '@/lib/dates';
import { errorMessage } from '@/lib/errors';
import { useProductLists } from '@/features/products/admin/api';
import { useCompetitions, useStandings, type Competition } from '../api';
import { useGamesMutations } from './api';

const FIELD = 'min-h-11 w-full rounded-sm border border-muted bg-surface px-3 text-body text-ink';

/** תחרויות: פתיחה, מעקב, סגירה והכרזה (PRD 5.8.2 ו 5.8.3). */
export function CompetitionsPage() {
  const confirm = useConfirm();
  const undo = useUndo();
  const canManage = usePerm('games.manage');
  const competitions = useCompetitions();
  const lists = useProductLists();
  const mutations = useGamesMutations();

  const [isAdding, setIsAdding] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!canManage) return <Navigate to="/admin" replace />;

  const run = (action: () => Promise<unknown>) => {
    setError(null);
    void action().catch((caught) => setError(errorMessage(caught)));
  };

  const channels = lists.data?.channels ?? [];

  return (
    <AdminPage
      title="תחרויות"
      subtitle="מי צובר הכי הרבה, ובאיזה חלון זמן"
      actions={
        !isAdding && (
          <Button variant="primary" onClick={() => setIsAdding(true)}>
            <Plus size={20} strokeWidth={1.75} aria-hidden="true" />
            תחרות חדשה
          </Button>
        )
      }
    >
      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      {isAdding && (
        <Panel title="תחרות חדשה">
          <form
            className="grid gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              const form = new FormData(event.currentTarget);
              const title = String(form.get('title') ?? '').trim();
              const starts = String(form.get('starts') ?? '');
              const ends = String(form.get('ends') ?? '');
              if (!title || !starts || !ends) return;
              if (new Date(ends) <= new Date(starts)) {
                setError('תאריך הסיום חייב להיות אחרי ההתחלה.');
                return;
              }
              run(async () => {
                await mutations.createCompetition.mutateAsync({
                  title,
                  description: String(form.get('description') ?? '').trim() || null,
                  starts_at: new Date(starts).toISOString(),
                  ends_at: new Date(ends).toISOString(),
                  scope: String(form.get('scope') ?? 'individual'),
                  prize: String(form.get('prize') ?? '').trim() || null,
                });
                setIsAdding(false);
              });
            }}
          >
            <div className="grid gap-1">
              <label htmlFor="title" className="text-body-sm text-muted">
                שם התחרות
              </label>
              <input id="title" name="title" className={FIELD} required />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="grid gap-1">
                <label htmlFor="starts" className="text-body-sm text-muted">
                  מתחילה
                </label>
                <input
                  id="starts"
                  name="starts"
                  type="datetime-local"
                  className={FIELD}
                  required
                />
              </div>
              <div className="grid gap-1">
                <label htmlFor="ends" className="text-body-sm text-muted">
                  מסתיימת
                </label>
                <input id="ends" name="ends" type="datetime-local" className={FIELD} required />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="grid gap-1">
                <label htmlFor="scope" className="text-body-sm text-muted">
                  בין מי
                </label>
                <select id="scope" name="scope" className={FIELD}>
                  <option value="individual">בין עובדים</option>
                  <option value="branch">בין סניפים</option>
                </select>
              </div>
              <div className="grid gap-1">
                <label htmlFor="prize" className="text-body-sm text-muted">
                  הפרס
                </label>
                <input id="prize" name="prize" className={FIELD} />
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button variant="primary" type="submit">
                פתיחת התחרות
              </Button>
              <Button onClick={() => setIsAdding(false)}>ביטול</Button>
            </div>
          </form>
        </Panel>
      )}

      {competitions.isPending && <Skeleton className="h-40" />}
      {competitions.isError && <ErrorState onRetry={() => void competitions.refetch()} />}

      {competitions.data?.length === 0 && !isAdding && (
        <EmptyState
          title="אין עדיין תחרויות"
          description="תחרות סופרת את הנקודות שנצברו בחלון הזמן שלה."
        />
      )}

      {competitions.data && competitions.data.length > 0 && (
        <Table>
          <thead>
            <Tr>
              <Th>שם</Th>
              <Th>חלון</Th>
              <Th>בין מי</Th>
              <Th>מצב</Th>
              <Th>
                <span className="sr-only">פעולות</span>
              </Th>
            </Tr>
          </thead>
          <tbody>
            {competitions.data
              .filter((competition) => !undo.isPending(competition.id))
              .map((competition) => (
              <Tr key={competition.id}>
                <Td>{competition.title}</Td>
                <Td className="text-label tabular">
                  {formatDateTime(competition.starts_at)} — {formatDateTime(competition.ends_at)}
                </Td>
                <Td>{competition.scope === 'branch' ? 'בין סניפים' : 'בין עובדים'}</Td>
                <Td>
                  <State competition={competition} />
                </Td>
                <Td>
                  <div className="flex flex-wrap items-center gap-1">
                    <Button
                      variant="ghost"
                      onClick={() => setOpen(open === competition.id ? null : competition.id)}
                    >
                      דירוג
                    </Button>

                    {isClosable(competition) && (
                      <Button
                        variant="primary"
                        onClick={() =>
                          run(() => mutations.closeCompetition.mutateAsync(competition.id))
                        }
                      >
                        <Trophy size={18} strokeWidth={1.75} aria-hidden="true" />
                        סגירה ובחירת זוכה
                      </Button>
                    )}

                    {hasWinner(competition) && channels.length > 0 && (
                      <Button
                        variant="secondary"
                        onClick={() => {
                          const channel = channels[0];
                          if (!channel) return;
                          confirm.ask({
                            title: 'הכרזת הזוכה',
                            tone: 'warn',
                            body: (
                              <p className="m-0">
                                ההודעה תגיע לכל מי שבערוץ "{channel.name}", ואי אפשר למחוק
                                אותה אחר כך.
                              </p>
                            ),
                            confirmLabel: 'הכרזת הזוכה בערוץ',
                            onConfirm: () =>
                              run(() =>
                                mutations.announceCompetition.mutateAsync({
                                  id: competition.id,
                                  channelId: channel.id,
                                }),
                              ),
                          });
                        }}
                      >
                        <Megaphone size={18} strokeWidth={1.75} aria-hidden="true" />
                        הכרזה בערוץ
                      </Button>
                    )}

                    <Button
                      variant="ghost"
                      aria-label="מחיקה"
                      onClick={() => {
                        undo.remove({
                          id: competition.id,
                          message: `"${competition.title}" נמחקה`,
                          perform: () => mutations.removeCompetition.mutateAsync(competition.id),
                        });
                      }}
                    >
                      <Trash2 size={20} strokeWidth={1.75} aria-hidden="true" />
                    </Button>
                  </div>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}

      {open && <StandingsPanel competitionId={open} />}
      {confirm.dialog}
    </AdminPage>
  );
}

const hasWinner = (c: Competition) =>
  c.winner_employee_id !== null || c.winner_branch_id !== null;

const isClosable = (c: Competition) => !hasWinner(c) && new Date(c.ends_at) <= new Date();

function State({ competition }: { competition: Competition }) {
  if (hasWinner(competition)) return <StatusBadge tone="good">נסגרה</StatusBadge>;
  if (new Date(competition.starts_at) > new Date()) {
    return <StatusBadge tone="warn">טרם התחילה</StatusBadge>;
  }
  if (new Date(competition.ends_at) > new Date()) {
    return <StatusBadge tone="warn">פעילה</StatusBadge>;
  }
  return <StatusBadge tone="bad">הסתיימה, לא נסגרה</StatusBadge>;
}

function StandingsPanel({ competitionId }: { competitionId: string }) {
  const standings = useStandings(competitionId);

  if (standings.isPending) return <Skeleton className="h-32" />;
  if (!standings.data) return null;

  const data = standings.data;
  const rows =
    data.scope === 'branch'
      ? data.branches.map((b) => ({ key: b.branch_id, name: b.name, meta: null, points: b.points }))
      : data.employees.map((e) => ({
          key: e.employee_id,
          name: e.name,
          meta: e.branch,
          points: e.points,
        }));

  return (
    <Panel title="הדירוג">
      {rows.length === 0 ? (
        <p className="m-0 text-body-sm text-muted">אף אחד לא צבר נקודות בחלון הזמן הזה.</p>
      ) : (
        <Table>
          <thead>
            <Tr>
              <Th>מקום</Th>
              <Th>שם</Th>
              <Th>סניף</Th>
              <Th>נקודות</Th>
            </Tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <Tr key={row.key}>
                <Td className="tabular">{i + 1}</Td>
                <Td>{row.name}</Td>
                <Td>{row.meta ?? '—'}</Td>
                <Td className="tabular">{row.points}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
    </Panel>
  );
}
